import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import Spinner from '../../shared/spinner';
import InputMask from 'react-input-mask';
import { format, parse, subDays } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import ArquivoService from '../../services/arquivoService';
import CustomAlert from '../../shared/customAlert';
import ComissaoService from '../../services/comissaoService'; // Importação correta

function RelatorioFaturasComponent() {
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);

  const [dtBase1, setDtBase1] = useState(format(subDays(new Date(), 15), 'dd/MM/yyyy'));
  const [dtBase2, setDtBase2] = useState(format(new Date(), 'dd/MM/yyyy'));

  const [selectTipoRelatorioAnalitico, setSelectTipoRelatorioAnalitico] = useState('SINTETICO');
  
  const [availableOrigens, setAvailableOrigens] = useState<any[]>([]);
  const [selectedOrigens, setSelectedOrigens] = useState<any[]>([]);
  const [selectedToMoveOrigens, setSelectedToMoveOrigens] = useState<any[]>([]);
  const [selectedFromSelectedOrigens, setSelectedFromSelectedOrigens] = useState<any[]>([]);

  const arquivoService = new ArquivoService();
  const comissaoService = new ComissaoService(); // Instância do novo serviço

  const listarOrigens = async (tipo: string) => {
    setLoading(true);
    try {
      const response = await arquivoService.listarOrigemGenerica(tipo);
      setAvailableOrigens(response.data);
      setSelectedOrigens([]);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  useEffect(() => {
    listarOrigens('PROMOTORA');
  }, []);

  const moveToSelectedOrigens = () => {
    const newSelected = [...selectedOrigens, ...selectedToMoveOrigens];
    const newAvailable = availableOrigens.filter(o => !selectedToMoveOrigens.includes(o));
    setAvailableOrigens(newAvailable);
    setSelectedOrigens(newSelected);
    setSelectedToMoveOrigens([]);
  };

  const moveToAvailableOrigens = () => {
    const newAvailable = [...availableOrigens, ...selectedFromSelectedOrigens];
    const newSelected = selectedOrigens.filter(o => !selectedFromSelectedOrigens.includes(o));
    setAvailableOrigens(newAvailable);
    setSelectedOrigens(newSelected);
    setSelectedFromSelectedOrigens([]);
  };

  const moveAllToSelectedOrigens = () => {
    setSelectedOrigens([...selectedOrigens, ...availableOrigens]);
    setAvailableOrigens([]);
  };

  const moveAllToAvailableOrigens = () => {
    setAvailableOrigens([...availableOrigens, ...selectedOrigens]);
    setSelectedOrigens([]);
  };

  const gerarRelatorio = async () => {
    if (selectedOrigens.length <= 0) return setAlert({ message: 'Selecione ao menos uma Promotora', type: 'warning' });
    if (!dtBase1 || !dtBase2) return setAlert({ message: 'Preencha as datas!', type: 'warning' });

    setLoading(true);
    try {
      // Converter datas para formato ISO (yyyy-MM-dd) esperado pelo backend
      const dataInicio = format(parse(dtBase1, "dd/MM/yyyy", new Date()), "yyyy-MM-dd");
      const dataFim = format(parse(dtBase2, "dd/MM/yyyy", new Date()), "yyyy-MM-dd");
      const idsPromotoras = selectedOrigens.map(o => o.id);
      const isDetalhado = selectTipoRelatorioAnalitico === 'DETALHADO';

      const response = await comissaoService.baixarRelatorioConsolidado(
        dataInicio, 
        dataFim, 
        idsPromotoras, 
        isDetalhado
      );
      
      // Tratar como PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const sufixo = isDetalhado ? 'Detalhado' : 'Sintetico';
      link.setAttribute('download', `Relatorio_Consolidado_${sufixo}.pdf`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setLoading(false);
      setAlert({ message: 'Relatório gerado com sucesso!', type: 'success' });
    } catch (err: any) {
      setLoading(false);
      setAlert({ message: 'Erro ao gerar relatório PDF.', type: 'error' });
      console.error(err);
    }
  };

  return (
    <>
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      
      <div className="position-relative m-md-3">
        {loading && <Spinner loading={loading} />}
        
        <div className="card" style={{ marginTop: '25px' }}>
          <div className="card-header bg-dark text-white">
            <strong>Relatório Consolidado de Faturas (PDF)</strong>
          </div>
          <div className="card-body">
            
            <div className="row mb-3">
              <div className="col-xl d-flex gap-4">
                <div className="form-check">
                  <input className="form-check-input" type="radio" id="sintetico"
                    checked={selectTipoRelatorioAnalitico === 'SINTETICO'}
                    onChange={() => setSelectTipoRelatorioAnalitico('SINTETICO')}
                  />
                  <label className="form-check-label" htmlFor="sintetico">Sintético</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="radio" id="detalhado"
                    checked={selectTipoRelatorioAnalitico === 'DETALHADO'}
                    onChange={() => setSelectTipoRelatorioAnalitico('DETALHADO')}
                  />
                  <label className="form-check-label" htmlFor="detalhado">Detalhado</label>
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-5">
                <label className="fw-bold mb-1">Período de Emissão <span className="text-danger">*</span></label>
                <div className="d-flex align-items-center gap-2">
                  <InputMask mask="99/99/9999" className="form-control" value={dtBase1} onChange={(e) => setDtBase1(e.target.value)} />
                  <span className="fw-bold">até</span>
                  <InputMask mask="99/99/9999" className="form-control" value={dtBase2} onChange={(e) => setDtBase2(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-md-5">
                <h6 className="fw-bold">Promotoras Disponíveis</h6>
                <select multiple className="form-control" style={{ height: '250px' }}
                  onChange={(e) => {
                    const values = Array.from(e.target.selectedOptions).map(opt => opt.value);
                    setSelectedToMoveOrigens(availableOrigens.filter(o => values.includes(o.id.toString())));
                  }}
                >
                  {availableOrigens.map(o => <option key={o.id} value={o.id}>{o.id} - {o.nome}</option>)}
                </select>
              </div>

              <div className="col-md-2 d-flex flex-column justify-content-center align-items-center gap-2">
                <button className="btn btn-sm btn-dark w-75" onClick={moveToSelectedOrigens} title="Mover selecionados">{'>'}</button>
                <button className="btn btn-sm btn-dark w-75" onClick={moveAllToSelectedOrigens} title="Mover todos">{'>>'}</button>
                <button className="btn btn-sm btn-outline-dark w-75" onClick={moveToAvailableOrigens} title="Remover selecionados">{'<'}</button>
                <button className="btn btn-sm btn-outline-dark w-75" onClick={moveAllToAvailableOrigens} title="Remover todos">{'<<'}</button>
              </div>

              <div className="col-md-5">
                <h6 className="fw-bold">Promotoras Selecionadas</h6>
                <select multiple className="form-control border-primary" style={{ height: '250px' }}
                  onChange={(e) => {
                    const values = Array.from(e.target.selectedOptions).map(opt => opt.value);
                    setSelectedFromSelectedOrigens(selectedOrigens.filter(o => values.includes(o.id.toString())));
                  }}
                >
                  {selectedOrigens.map(o => <option key={o.id} value={o.id}>{o.id} - {o.nome}</option>)}
                </select>
                <small className="text-muted">{selectedOrigens.length} selecionada(s)</small>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-md-3">
                <button 
                  className="btn btn-dark w-100 d-flex align-items-center justify-content-center gap-2 shadow" 
                  style={{ height: '50px' }} 
                  onClick={gerarRelatorio}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faPrint} />
                  <strong>IMPRIMIR PDF</strong>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RelatorioFaturasComponent;